import React, {useEffect, useRef, useState} from 'react';
import {FiEdit3, FiPlus, FiSend, FiX} from 'react-icons/fi';
import Swal from 'sweetalert2';
import {DocumentRequestCreate, TagCreate, TagType} from '../../../schemas';
import {ChatListItem, SendDocumentRequestsPayload} from '../../../schemas/chat';
import {useUserContext} from '../../../contexts/user_context';
import {useGroupContext} from '../../../contexts/group_context';
import {ButtonColors, TextColors} from '../../../colors';


interface DocumentRequestFormProps {
    currentChat: ChatListItem;
    sendDocumentRequests: (payload: SendDocumentRequestsPayload) => void;
}

const DocumentRequestForm: React.FC<DocumentRequestFormProps> = ({currentChat, sendDocumentRequests}) => {
    const {currentGroup} = useGroupContext()!;
    const {currentUser} = useUserContext()!;

    const [caseBackground, setCaseBackground] = useState('');
    const [descriptions, setDescriptions] = useState<string[]>(['']);

    const [rawMode, setRawMode] = useState(false);
    const [rawInput, setRawInput] = useState('');
    const endRef = useRef<HTMLDivElement | null>(null);

    // whenever layout-changing fields update, nudge to bottom
    useEffect(() => {
        endRef.current?.scrollIntoView({block: 'end', inline: 'nearest'});
    }, [descriptions.length, rawMode, caseBackground]);

    const handleAddDescription = () => setDescriptions([...descriptions, '']);

    const handleUpdateDescription = (index: number, value: string) => {
        const updated = [...descriptions];
        updated[index] = value;
        setDescriptions(updated);
    };

    const parseRawInput = (input: string): string[] => {
        const lines = input
            .split(/\r?\n/)
            .map(line => line.trimEnd());

        const bulletPatterns = [
            /^(\d+[.)])\s*/i,      // 1. or 1)
            /^([a-zA-Z][.)])\s*/i, // a. or A)
            /^([-•*])\s*/          // -, •, *
        ];

        // Detect first matching bullet pattern
        let detectedPattern: RegExp | null = null;
        for (const line of lines) {
            for (const pattern of bulletPatterns) {
                if (pattern.test(line)) {
                    detectedPattern = pattern;
                    break;
                }
            }
            if (detectedPattern) break;
        }

        if (!detectedPattern) {
            // No bullet pattern found: fallback to paragraph split
            return input
                .split(/\n{2,}/)
                .map(block => block.trim())
                .filter(block => block.length > 0);
        }

        // Split into bullet-grouped blocks
        const blocks: string[] = [];
        let currentBlock = '';

        for (const line of lines) {
            if (detectedPattern.test(line)) {
                if (currentBlock) {
                    blocks.push(currentBlock.trim());
                }
                currentBlock = line;
            } else {
                currentBlock += '\n' + line;
            }
        }

        if (currentBlock) {
            blocks.push(currentBlock.trim());
        }

        return blocks;
    };


    const handleRawSubmit = () => {
        const parsed = parseRawInput(rawInput);
        if (parsed.length > 0) {
            setDescriptions(parsed);
        }
        setRawInput('');
        setRawMode(false);
    };

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const cleanedDescriptions = descriptions
            .map(d => d.trim())
            .filter(d => d.length > 0);

        if (cleanedDescriptions.length === 0) {
            Swal.fire({
                title: 'Document Request Unchanged',
                text: 'No changes detected in the document request.',
                icon: 'info',
            });
        }

        try {
            const tags: TagCreate[] = cleanedDescriptions.map(desc => ({
                tag_name: desc,
                type: TagType.REQUEST_SET,
                group_id: currentGroup!.id,
                submitter_id: currentUser!.id,
                description: desc,
                system_context: caseBackground,
                chat_id: currentChat.id
            }));
            const newDocumentRequest: DocumentRequestCreate = {
                chat_id: currentChat.id,
                name: `Document Request for Chat ${currentChat.title || currentChat.id}`,
                description: caseBackground,
                group_id: currentGroup!.id,
                submitter_id: currentUser!.id,
            }
            sendDocumentRequests({
                tagsToInfer: tags,
                documentRequest: newDocumentRequest
            } as SendDocumentRequestsPayload);
            setCaseBackground("");
            setDescriptions(['']);
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <form onSubmit={onSubmit} className="p-4 pt-2 border-t bg-white">
            <p className="text-sm text-gray-600 text-left mx-2 mb-1">Case Overview</p>
            <textarea
                value={caseBackground}
                onChange={e => setCaseBackground(e.target.value)}
                placeholder="Provide background information about the case..."
                rows={2}
                className="w-full border rounded shadow-sm focus:shadow-lg focus:outline-none px-4 py-2 text-sm text-gray-700 transition m-2 mb-3"
            />

            <div className="flex items-center justify-between mx-2 mb-1">
                <p className="text-sm text-gray-600">Document Requests</p>
                <button
                    type="button"
                    onClick={() => {
                        if (rawMode) {
                            handleRawSubmit();
                        } else {
                            setRawInput(descriptions.join('\n'));
                            setRawMode(true);
                        }
                    }}
                    className="flex items-center gap-1 text-sm text-blue-600 hover:underline transition"
                >
                    <FiEdit3 className="w-4 h-4"/>
                    {rawMode ? "Done" : "Raw Text"}
                </button>
            </div>

            {rawMode ? (
                <textarea
                    value={rawInput}
                    onChange={e => setRawInput(e.target.value)}
                    onBlur={handleRawSubmit}
                    onKeyDown={e => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleRawSubmit();
                        }
                    }}
                    placeholder="Separate requests using bullets (e.g. '-', '*', '•') or numbers (e.g. '1.', 'a)'). Multi-line entries are supported."
                    rows={5}
                    className="w-full border rounded shadow-sm focus:shadow-lg focus:outline-none px-4 py-2 text-sm text-gray-700 transition m-2"
                />
            ) : (
                <>
                    {descriptions.map((desc, index) => (
                        <div key={index} className="flex items-center gap-2 mx-2 mb-1">
        <textarea
            value={desc}
            onChange={e => handleUpdateDescription(index, e.target.value)}
            rows={2}
            className="flex-grow border rounded shadow-sm focus:shadow-lg focus:outline-none px-4 py-2 text-sm text-gray-700 transition"
        />
                            {descriptions.length > 1 && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        const updated = [...descriptions];
                                        updated.splice(index, 1);
                                        setDescriptions(updated);
                                    }}
                                    className="text-gray-400 hover:text-red-500 transition p-1"
                                    title="Remove"
                                >
                                    <FiX className="w-4 h-4"/>
                                </button>
                            )}
                        </div>
                    ))}
                    <div className="flex justify-start ml-2 mt-2">
                        <button
                            type="button"
                            onClick={handleAddDescription}
                            className="flex items-center text-sm text-blue-600 hover:underline transition"
                        >
                            <FiPlus className="mr-1"/> Add another
                        </button>
                    </div>
                </>
            )}
            <div className="flex justify-between pb-2 mr-2">
                <div className="flex items-center"/>
                <button
                    type="submit"
                    className={`${ButtonColors.dark} ${ButtonColors.hoverDark} ${TextColors.light} flex rounded px-3 py-2 shadow-sm transition-all`}
                >
                    <FiSend className="flex w-5 h-5"/>
                </button>
            </div>
            <div ref={endRef} className="h-0 scroll-mb-24"/>
        </form>
    );
};

export default DocumentRequestForm;
